import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import { client, generateWidth } from '../../util/cloudinary';
import * as Styled from './Gif.styled';

/**
 * 'Gif' transforms gif assets through Cloudinary into a video file.
 * The new video file plays on loop without audio or playback controls.
 */
const Gif = ({ className, cropType, src, alt, background, crop, isLazy }) => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const ConditionalLazyWrapper = isLazy ? Styled.LazyLoad : React.Fragment;

  return (
    <Styled.Container
      className={className}
      aspectRatio={crop.height && crop.width ? crop.height / crop.width : 1}
      hasLoaded={videoLoaded}
    >
      <ConditionalLazyWrapper>
        <Styled.Video
          alt={alt}
          loop
          preload="metadata"
          autoPlay
          muted
          playsInline
          onPlay={() => !videoLoaded && setVideoLoaded(true)}
          hasLoaded={videoLoaded}
        >
          <source
            src={client.url(src, {
              type: 'fetch',
              quality: 'auto',
              transformation: [
                { fetchFormat: 'mp4' },
                { audioCodec: 'none' },
                { crop: cropType, ...crop },
                {
                  background,
                  width: generateWidth(crop),
                  crop: 'scale'
                }
              ]
            })}
            type="video/mp4"
          />
        </Styled.Video>
      </ConditionalLazyWrapper>
    </Styled.Container>
  );
};

Gif.propTypes = {
  /**
   * CSS Classname
   */
  className: PropTypes.string,
  /**
   * The type of crop
   *
   * @see @link {https://cloudinary.com/documentation/image_transformations#resizing_and_cropping_images}
   */
  cropType: PropTypes.oneOf([
    'scale',
    'fit',
    'limit',
    'mfit',
    'pad',
    'lpad',
    'mpad',
    'crop'
  ]),
  /**
   * remote image url
   */
  src: PropTypes.string,
  /**
   * alternate text for `video` tag
   */
  alt: PropTypes.string,
  /**
   * background color of the image
   */
  background: PropTypes.string,
  /**
   * optional crop applied before any other image transformations
   */
  crop: PropTypes.shape({
    height: PropTypes.number,
    width: PropTypes.number
  }),
  /**
   * If `true`, image is lazily loaded.
   * @default true
   */
  isLazy: PropTypes.bool
};

Gif.defaultProps = {
  className: null,
  cropType: 'crop',
  alt: null,
  background: convertColorToThemeHexCode('white'),
  crop: {},
  isLazy: true,
  src: null
};

export default Gif;
